import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  Sparkles, Trophy, Terminal, ArrowRight, CheckCircle2, FileCode2, Braces, Globe, Cpu, Coffee, Megaphone, X,
  Swords, Flame, Map as MapIcon, Layers, BarChart3, Brain, TerminalSquare, AppWindow,
  ShieldCheck, ClipboardList, Server, MonitorSmartphone, FileCode, Factory,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import AdBanner from "@/components/AdBanner";
import BitAvatar from "@/components/bit/BitAvatar";
import Logo from "@/components/Logo";

const ADS_DISCLAIMER_KEY = "devpath_ads_disclaimer_dismissed";

const langs = [
  {
    name: "Python",
    icon: FileCode2,
    color: "#0055FF",
    blurb: "Heute vor allem in KI & Machine Learning, Datenanalyse und Automatisierung im Einsatz – z. B. bei Werkzeugen rund um ChatGPT, Instagram-Backend oder Forschungssoftware. Auch bei Einsteigern beliebt, weil der Code sehr lesbar ist.",
  },
  {
    name: "JavaScript",
    icon: Braces,
    color: "#FFD830",
    blurb: "Die Sprache des Webs – läuft in jedem Browser und macht Webseiten interaktiv. Mit Frameworks wie React/Vue baut man damit ganze Web-Apps, mit Node.js läuft sie auch auf dem Server.",
  },
  {
    name: "HTML & CSS",
    icon: Globe,
    color: "#FF5C00",
    blurb: "Das Fundament jeder Webseite im Internet. HTML strukturiert Inhalte, CSS gestaltet sie visuell – ohne die beiden gäbe es keine einzige Webseite, egal mit welcher Programmiersprache sie sonst gebaut ist.",
  },
  {
    name: "C++",
    icon: Cpu,
    color: "#00D05A",
    blurb: "Wird genutzt, wo maximale Geschwindigkeit zählt: Spiele-Engines (z. B. Unreal Engine), Betriebssysteme, Browser-Engines und Hochleistungssoftware wie Trading-Systeme.",
  },
  {
    name: "Java",
    icon: Coffee,
    color: "#FF3333",
    blurb: "Basis für die meisten Android-Apps und für große Unternehmenssoftware (Banken, Versicherungen, Konzerne). Bekannt für 'einmal schreiben, überall ausführen' dank der Java Virtual Machine.",
  },
];

const topics = [
  {
    name: "KI-Prompting",
    icon: Brain,
    color: "#9D4EDD",
    blurb: "Gute Prompts zu schreiben ist mittlerweile in fast jedem Job nützlich. Lerne, wie du ChatGPT, Claude & Co. so anweist, dass du wirklich brauchbare Antworten bekommst – ganz ohne Programmierkenntnisse.",
  },
  {
    name: "Linux-Befehle",
    icon: TerminalSquare,
    color: "#2EC4B6",
    blurb: "Die meisten Server und Cloud-Systeme im Internet laufen mit Linux. Lerne die wichtigsten Terminal-Befehle einfach erklärt – ein nützliches Grundwissen für jede IT-Laufbahn.",
  },
  {
    name: "Windows-Themen",
    icon: AppWindow,
    color: "#0078D4",
    blurb: "CMD, PowerShell und nützliche Bordmittel von Windows – praktisch für den Büroalltag, IT-Support oder einfach um den eigenen PC besser zu verstehen.",
  },
  {
    name: "IT-Sicherheit",
    icon: ShieldCheck,
    color: "#D62839",
    blurb: "Starke Passwörter, Phishing erkennen, Backups & 2FA – die wichtigsten Grundlagen, um dich und deine Daten im Alltag zu schützen.",
  },
  {
    name: "Projektmanagement",
    icon: ClipboardList,
    color: "#F4A261",
    blurb: "Scrum, Kanban, Meilensteine & Risikomanagement – die Grundlagen, um Projekte jeder Größe erfolgreich zu planen und umzusetzen.",
  },
  {
    name: "PHP",
    icon: Server,
    color: "#777BB4",
    blurb: "Die Klassiker-Sprache für Web-Backends – mit echtem, ausführbarem Code wie bei Python und Java direkt im Browser lernen.",
  },
  {
    name: "PC-Technologie",
    icon: MonitorSmartphone,
    color: "#4CC9F0",
    blurb: "CPU, RAM, SSD & Co. einfach erklärt – verstehe, was in deinem PC steckt und worauf es beim Kauf wirklich ankommt.",
  },
  {
    name: "XML",
    icon: FileCode,
    color: "#FF8FA3",
    blurb: "Elemente, Attribute und Struktur – lerne, wie Daten mit XML beschrieben werden, und wie es sich von JSON unterscheidet.",
  },
  {
    name: "PEARL",
    icon: Factory,
    color: "#94A3B8",
    blurb: "Eine historische deutsche Echtzeit-Sprache für Industrieautomatisierung – die Grundideen von Echtzeit-Programmierung einfach erklärt.",
  },
];

const features = [
  { icon: Terminal, title: "Interaktiver Editor", text: "Schreibe Code direkt im Browser und sieh sofort das Ergebnis.", color: "#00D05A" },
  { icon: Sparkles, title: "Lernroboter Bit", text: "Bit erklärt dir Code Zeile für Zeile, gibt Tipps in deinem Tempo und freut sich mit dir über jede gelöste Aufgabe.", color: "#0055FF" },
  { icon: Trophy, title: "XP & Streaks", text: "Sammle Punkte, halte deine Lernsträhne und bleib motiviert.", color: "#FFD830" },
];

const newFeatures = [
  { icon: Swords, title: "Coding-Challenges", text: "Lös automatisch bewertete Aufgaben in Python, C++ und Java – mit sofortigem Feedback pro Testfall.", color: "#FF5C00" },
  { icon: Flame, title: "Tages-Challenge", text: "Jeden Tag eine neue Aufgabe. Löse sie und sammle Bonus-XP für deine eigene Tages-Serie.", color: "#FFD830" },
  { icon: MapIcon, title: "Lernpfad", text: "Verfolge deinen Weg durch alle Kurse als verbundene Lernkette – erledigt, aktuell, noch offen.", color: "#00D05A" },
  { icon: Layers, title: "Flashcards", text: "Wiederhole Konzepte mit Spaced Repetition (SM-2) – Karten kommen genau dann zurück, wenn du sie brauchst.", color: "#0055FF" },
  { icon: BarChart3, title: "Fortschritts-Dashboard", text: "XP-Aufteilung, Kursfortschritt und alle Statistiken übersichtlich in einem Dashboard.", color: "#FF3333" },
];

export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [activeLang, setActiveLang] = useState(null);

  useEffect(() => {
    if (user) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  useEffect(() => {
    if (!localStorage.getItem(ADS_DISCLAIMER_KEY)) {
      setShowDisclaimer(true);
    }

    // AdSense Script dynamisch laden
    const scriptId = "adsense-script";
    if (!document.getElementById(scriptId)) {
      const script = document.createElement("script");
      script.id = scriptId;
      script.async = true;
      script.src = "https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-4005237431516670";
      script.crossOrigin = "anonymous";
      document.head.appendChild(script);
    }
  }, []);

  const dismissDisclaimer = () => {
    localStorage.setItem(ADS_DISCLAIMER_KEY, "1");
    setShowDisclaimer(false);
  };

  return (
    <div className="min-h-screen">
      {showDisclaimer && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="ads-disclaimer">
          <div className="nb-card p-6 sm:p-8 max-w-md text-center space-y-4 animate-pop">
            <div className="h-12 w-12 mx-auto rounded-xl border border-warning/50 bg-warning/10 flex items-center justify-center">
              <Megaphone className="h-6 w-6 text-warning" strokeWidth={2.2} />
            </div>
            <h2 className="font-heading font-bold text-xl text-foreground">Hinweis zu Werbung</h2>
            <p className="text-sm text-muted-foreground">
              Diese Webseite nutzt kleine Werbebanner zur Unterhaltung des Betriebs von DevPath.
              Die Lerninhalte selbst bleiben für dich kostenlos.
            </p>
            <button
              onClick={dismissDisclaimer}
              data-testid="ads-disclaimer-dismiss"
              className="bg-primary font-semibold px-5 py-3 rounded-xl w-full glow"
            >
              Verstanden
            </button>
          </div>
        </div>
      )}

      {activeLang && (
        <div
          className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
          data-testid="lang-info-modal"
          onClick={() => setActiveLang(null)}
        >
          <div className="nb-card p-6 sm:p-8 max-w-md text-center space-y-4 animate-pop" onClick={(e) => e.stopPropagation()}>
            <div
              className="h-14 w-14 mx-auto rounded-xl border flex items-center justify-center"
              style={{ borderColor: activeLang.color, backgroundColor: `${activeLang.color}22` }}
            >
              <activeLang.icon className="h-7 w-7" style={{ color: activeLang.color }} strokeWidth={2.2} />
            </div>
            <h2 className="font-heading font-bold text-2xl text-foreground">{activeLang.name}</h2>
            <p className="text-sm text-muted-foreground leading-relaxed">{activeLang.blurb}</p>
            <button
              onClick={() => setActiveLang(null)}
              data-testid="lang-info-close"
              className="flex items-center justify-center gap-2 border border-input text-foreground font-semibold px-5 py-3 rounded-xl w-full hover:bg-muted"
            >
              <X className="h-4 w-4" strokeWidth={2.2} />
              Schließen
            </button>
          </div>
        </div>
      )}

      {/* Nav */}
      <header className="sticky top-0 z-40 border-b border-line bg-background/85 backdrop-blur-md">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <Logo />
            <span className="hidden sm:inline font-heading font-bold text-xl tracking-tight text-foreground">DevPath</span>
          </div>
          <div className="flex items-center gap-1 sm:gap-2">
            <Link to="/login" data-testid="nav-login" className="font-semibold text-sm sm:text-base px-3 sm:px-4 py-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted">
              Anmelden
            </Link>
            <Link to="/register" data-testid="nav-register" className="bg-primary font-semibold text-sm sm:text-base px-3 sm:px-4 py-2 rounded-lg glow">
              Registrieren
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-14 lg:py-20 grid lg:grid-cols-2 gap-12 items-center">
        <div className="animate-fade-up">
          <p className="eyebrow mb-4">// Lernplattform für IT &amp; Programmierung</p>
          <h1 className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.03] text-foreground">
            Lerne programmieren an deiner eigenen <span className="text-primary">Schaltzentrale</span>.
          </h1>
          <p className="text-lg text-muted-foreground mt-6 max-w-lg leading-relaxed">
            Jede gelöste Aufgabe bestromt eine Leiterbahn. 14 Kurse von Python bis IT-Sicherheit, ein Editor direkt im
            Browser und Bit, ein Lernroboter, der mitdenkt.
          </p>
          <div className="flex flex-wrap items-center gap-3 mt-8">
            <Link to="/register" data-testid="hero-cta" className="flex items-center gap-2 bg-primary font-semibold text-lg px-6 py-3.5 rounded-xl glow nb-press">
              Jetzt kostenlos starten
              <ArrowRight className="h-5 w-5" strokeWidth={2.2} />
            </Link>
            <Link to="/login" data-testid="hero-login" className="font-semibold text-lg px-6 py-3.5 rounded-xl border border-input text-foreground hover:bg-muted">
              Anmelden
            </Link>
          </div>
          <div className="flex flex-wrap gap-5 mt-7 text-sm font-medium text-muted-foreground">
            <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-accent" strokeWidth={2.2} /> 14 Kurse</span>
            <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-accent" strokeWidth={2.2} /> Kostenlos</span>
            <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-accent" strokeWidth={2.2} /> Lernroboter Bit</span>
          </div>
        </div>
        <div className="flex justify-center animate-fade-up" style={{ animationDelay: "0.15s" }}>
          <div className="relative w-[340px] max-w-full aspect-square rounded-[28px] border border-line bg-card flex items-center justify-center overflow-hidden">
            <div className="absolute inset-x-6 top-1/2 h-0.5 bg-line" />
            <div className="absolute inset-x-6 top-1/2 h-0.5 trace-flow" />
            <div className="absolute inset-y-6 left-1/2 w-0.5 bg-line" />
            <span className="absolute left-6 top-[calc(50%-5px)] h-2.5 w-2.5 rounded-full bg-primary glow" />
            <span className="absolute right-6 top-[calc(50%-5px)] h-2.5 w-2.5 rounded-full bg-secondary glow-bit" />
            <div className="relative bit-bob">
              <BitAvatar size={210} mood="happy" />
            </div>
            <span className="absolute bottom-5 right-5 rounded-lg border border-accent/60 bg-[#0F1626] px-3 py-1.5 font-mono text-sm font-semibold text-accent">
              +50 XP
            </span>
          </div>
        </div>
      </section>

      {/* Languages */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <p className="eyebrow mb-2">01 / Sprachen</p>
        <h2 className="font-heading font-bold text-2xl sm:text-3xl mb-6 text-foreground">Diese Sprachen lernst du</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {langs.map((l) => {
            const Icon = l.icon;
            return (
              <button
                key={l.name}
                type="button"
                onClick={() => setActiveLang(l)}
                data-testid={`lang-card-${l.name.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
                className="nb-card nb-press p-5 flex flex-col items-center gap-3 text-center"
              >
                <div className="h-14 w-14 rounded-xl border flex items-center justify-center" style={{ borderColor: `${l.color}99`, backgroundColor: `${l.color}1f` }}>
                  <Icon className="h-7 w-7" style={{ color: l.color }} strokeWidth={2.2} />
                </div>
                <span className="font-semibold text-foreground">{l.name}</span>
              </button>
            );
          })}
        </div>
      </section>

      {/* Topics */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <p className="eyebrow mb-2">02 / Themen</p>
        <h2 className="font-heading font-bold text-2xl sm:text-3xl mb-6 text-foreground">Das lernst du außerdem</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {topics.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.name}
                type="button"
                onClick={() => setActiveLang(t)}
                data-testid={`topic-card-${t.name.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
                className="nb-card nb-press p-5 flex items-center gap-4 text-left"
              >
                <div className="h-12 w-12 shrink-0 rounded-xl border flex items-center justify-center" style={{ borderColor: `${t.color}99`, backgroundColor: `${t.color}1f` }}>
                  <Icon className="h-6 w-6" style={{ color: t.color }} strokeWidth={2.2} />
                </div>
                <span className="font-semibold text-foreground">{t.name}</span>
              </button>
            );
          })}
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <AdBanner field="landing_snippet" className="mx-auto" />
      </div>

      {/* Features */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
        <p className="eyebrow mb-2">03 / Werkzeuge</p>
        <h2 className="font-heading font-bold text-2xl sm:text-3xl mb-6 text-foreground">Alles für deinen Lernweg</h2>
        <div className="grid md:grid-cols-3 gap-5">
          {features.map((f) => {
            const Icon = f.icon;
            return (
              <div key={f.title} className="nb-card p-6">
                <Icon className="h-7 w-7 mb-4 text-primary" strokeWidth={2.2} />
                <h3 className="font-heading font-bold text-xl mb-1 text-foreground">{f.title}</h3>
                <p className="text-muted-foreground leading-relaxed">{f.text}</p>
              </div>
            );
          })}
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 mt-5">
          {newFeatures.map((f) => {
            const Icon = f.icon;
            return (
              <div key={f.title} data-testid={`new-feature-${f.title.toLowerCase().replace(/[^a-z0-9]/g, "-")}`} className="nb-card p-5 flex flex-col gap-3">
                <Icon className="h-6 w-6 text-secondary" strokeWidth={2.2} />
                <h3 className="font-heading font-semibold text-lg text-foreground">{f.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{f.text}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-16">
        <div className="relative overflow-hidden rounded-3xl border border-primary/60 bg-card p-8 sm:p-12 text-center glow">
          <div className="absolute inset-x-0 top-0 h-0.5 trace-flow" />
          <div className="mx-auto mb-4 w-fit bit-bob">
            <BitAvatar size={88} />
          </div>
          <h2 className="font-heading font-bold text-3xl sm:text-4xl mb-3 text-foreground">Bereit für deine erste Leiterbahn?</h2>
          <p className="text-lg text-muted-foreground mb-7">Erstelle ein kostenloses Konto und schreibe deine erste Zeile Code. Bit wartet schon.</p>
          <Link to="/register" data-testid="cta-register" className="inline-flex items-center gap-2 bg-primary font-semibold text-lg px-6 py-3.5 rounded-xl nb-press">
            Jetzt kostenlos starten
            <ArrowRight className="h-5 w-5" strokeWidth={2.2} />
          </Link>
        </div>
      </section>

      <footer className="border-t border-line py-8 text-center text-sm font-medium text-muted-foreground">
        <div className="max-w-6xl mx-auto px-4 flex flex-col sm:flex-row items-center justify-between gap-4">
          <span>© {new Date().getFullYear()} DevPath – Programmieren lernen, spielerisch.</span>
          <div className="flex flex-wrap items-center justify-center gap-5">
            <a
              href="https://www.buymeacoffee.com/kaidrabot"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="buy-me-a-coffee-link"
              className="flex items-center gap-1.5 rounded-lg border border-warning/50 px-3 py-1.5 text-warning hover:bg-warning/10"
            >
              <Coffee className="h-4 w-4" strokeWidth={2.2} />
              Kaffee spendieren
            </a>
            <Link to="/impressum" className="hover:text-foreground">Impressum</Link>
            <Link to="/datenschutz" className="hover:text-foreground">Datenschutz</Link>
            <a href="mailto:kontakt@kaidra.app" className="hover:text-foreground">Kontakt</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
